import { apiGet, apiPost } from '@forwardflow/services/Axios/configs'
import CampaignService from '@forwardflow/services/CampaignService'
import { FubService } from '@forwardflow/services/fub.service'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'
import { ICampaignV2Api } from '@forwardflow/features/campaign-v2/services/campaign-v2.api'
import { liveCampaignV2Api } from '@forwardflow/features/campaign-v2/services/campaign-v2.live'
import { idempotencyKey } from '@forwardflow/features/campaign-v2/utils/config'
import {
   CampaignV2Error,
   IAssistant,
   IAvailableNumber,
   ICadenceStep,
   ICatalogItem,
   IConfigurationRead,
   IContentPool,
   IContentReference,
   IContentRevision,
   IConversationDefinition,
   IPhoneDefinition,
   IStrategyDefinition,
   TContentKind,
   ICriteria,
   ILiveCampaign,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   CADENCE_PRESETS,
   NUMBER_PRICE_PER_MONTH,
   ORG,
   RECOMMENDED_BY_TYPE,
   RECOMMENDED_PLAYBOOK,
   SAMPLE_LEAD,
} from '@forwardflow/features/campaign-flow/data/mock'
import { UserService } from '@forwardflow/services/UserService'
import {
   IActivityItem,
   IAreaCode,
   IAudience,
   ICampaignStats,
   IContentOption,
   ICampaignRow,
   IFilterRow,
   IFlowDraft,
   IMetric,
   IOverviewStats,
   IPhoneNumberOption,
   IPlaybook,
   IResults,
   ITouch,
   IVoiceOption,
   TActivityKind,
   TCadencePreset,
   TCampaignKind,
   TLeadType,
   TPeriod,
   TPlaybookContent,
   TRailKey,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { ICampaignFlowService } from '@forwardflow/features/campaign-flow/services/campaign-flow.service'
import {
   LEAD_TYPES,
   LEAD_TYPE_LABEL,
   TFilterVocabulary,
   audienceTypes,
   interpretDescription,
} from '@forwardflow/features/campaign-flow/utils/describe'
import { emptyDraft } from '@forwardflow/features/campaign-flow/store/flow.store'
import {
   pickContent,
   sortTouches,
   syncMessages,
   viewFor,
   withTypes,
} from '@forwardflow/features/campaign-flow/utils/playbook'
import {
   PICK,
   TDayRow,
   buckets,
   countQueries,
   kindCriteria,
   lastActive,
   localHour,
   num,
   periodWindows,
   ratioMetric,
   timeFor,
   windowMetric,
} from '@forwardflow/features/campaign-flow/utils/live-derive'
import { parseDate } from '@forwardflow/features/campaign-flow/utils/links'
import {
   clockTime,
   fromTemplate,
   plural,
} from '@forwardflow/features/campaign-flow/utils/text'
import {
   IContentSnapshot,
   messagesFromStrategy,
   playbookFromContent,
   scriptFromPhone,
} from '@forwardflow/features/campaign-flow/utils/from-content'
import {
   localReadiness,
   reasonToItem,
} from '@forwardflow/features/campaign-flow/utils/readiness'
import {
   IPlaybookRefs,
   TBusinessTypeIds,
   TPlaybookRefsByType,
   toConfiguration,
   toE164,
   toPhoneDefinition,
   toStrategyDefinition,
} from '@forwardflow/features/campaign-flow/utils/to-configuration'

/**
 * The guided flow on the real backend (campaign v2 handoff; plan in the
 * "Campaign flow · API integration plan" doc).
 *
 * Every write goes through the existing v2 adapter (`campaign-v2.live.ts`),
 * so URLs, envelopes and errors are handled in one place. This file only
 * decides the order of calls and remembers what the backend handed back.
 */

/* ---------- What one flow session remembers ---------- */

type TContentSlot = 'phone' | 'strategy' | 'conversation'

interface IBoundContent {
   categoryId: number
   /** Per slot: a fingerprint of the words, to know when the words changed. */
   hashes: Record<TContentSlot, string>
   refs: IPlaybookRefs
   /** The inbound script can greet callers the engine has not matched to a lead (production requires it). */
   inboundForUnknown?: boolean
}

export interface ILiveSession {
   campaignId?: string
   revision?: number
   /** One key per deliberate create, reused if the first try never answered. */
   createKey?: string
   activateKey?: string
   /** Library items behind the recommended words, per business category id. */
   recommendedBy?: Record<string, IBoundContent>
   /** Library items holding this campaign's words, per business category id. */
   boundBy?: Record<string, IBoundContent>
   assistant?: { id: string | number; voiceId: string; name: string }
   /** Numbers reserved for this campaign (E.164). Bought at launch. */
   reserved?: string[]
   /** Numbers the organization already owned that are attached to this campaign (E.164). */
   assigned?: string[]
   /** A campaign made before the flow: no configuration to save, only the assistant. */
   legacy?: boolean
}

const SESSION_KEY = 'ff-campaign-flow-live'

const readSession = (): ILiveSession => {
   try {
      return JSON.parse(
         sessionStorage.getItem(SESSION_KEY) || '{}'
      ) as ILiveSession
   } catch {
      return {}
   }
}
const writeSession = (s: ILiveSession) => {
   try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(s))
   } catch {
      /* private mode: this tab still works from memory */
   }
}
let session: ILiveSession = readSession()
const remember = (patch: Partial<ILiveSession>) => {
   session = { ...session, ...patch }
   writeSession(session)
}

/** "New campaign" starts a fresh session; nothing from the last one is reused. */
export const startNewLiveSession = () => {
   session = {}
   writeSession(session)
   forgetReads()
}

/** For tests. */
export const liveSessionNow = () => session

/* ---------- Small helpers ---------- */

let api: ICampaignV2Api = liveCampaignV2Api
/** Tests swap in a fake adapter. */
export const setApiForTests = (a: ICampaignV2Api) => {
   api = a
   categoryCache = null
   voiceCache = null
   recommendedCache.clear()
   seen.clear()
}

const orgId = () => useAppStore.getState().currentOrganization || ''

/** Stable orb hue from a voice id, so a voice keeps its colour everywhere. */
export const hueOf = (id: string) => {
   let h = 0
   for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 360
   return h
}

/** "+17195550140" → "(719) 555-0140" */
export const toDisplay = (e164: string) => {
   const d = e164.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
   return d.length === 10
      ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
      : e164
}
const areaOf = (e164: string) =>
   e164
      .replace(/\D/g, '')
      .replace(/^1(?=\d{10}$)/, '')
      .slice(0, 3)

/*
 * Reads that several parts of a screen ask for at the same time (the campaign
 * list, recent conversations, the number inventory, the voices) are fetched
 * once and kept for a few seconds; every write forgets them.
 */
const recent = new Map<string, { at: number; value: Promise<unknown> }>()
const fresh = <T>(key: string, ttlMs: number, load: () => Promise<T>) => {
   const hit = recent.get(key)
   if (hit && Date.now() - hit.at < ttlMs) return hit.value as Promise<T>
   const value = load().catch((e) => {
      recent.delete(key)
      throw e
   })
   recent.set(key, { at: Date.now(), value })
   return value
}
const forgetReads = () => recent.clear()

/** The campaign list; the backend answers 404 "No active campaigns" when there are none. */
/** This endpoint represents an empty page as a specific 404, not an empty array. */
const noCampaigns = (error: unknown) => {
   const e = error as {
      response?: { status?: number; data?: { message?: string } }
   }
   return (
      e?.response?.status === 404 &&
      e.response.data?.message === 'No active campaigns'
   )
}

/** Every campaign, 50 a page, in every status; a repeated page is a backend fault, not the end. */
const allCampaignRows = async () => {
   const result: Record<string, unknown>[] = []
   const ids = new Set<string>()
   for (let page = 1; ; page += 1) {
      let batch: Record<string, unknown>[]
      try {
         batch = listOf(
            await CampaignService.getActiveCampaigns({
               page,
               limit: 50,
               status: 'active,paused,draft,inactive',
            })
         )
      } catch (error) {
         if (noCampaigns(error)) return result
         throw error
      }
      const unseen = batch.filter((row) => !ids.has(String(row.id)))
      if (batch.length && !unseen.length)
         throw new Error(
            'Could not load the next page of campaigns. Please retry.'
         )
      unseen.forEach((row) => {
         ids.add(String(row.id))
         result.push(row)
      })
      if (batch.length < 50) return result
   }
}

/** The campaign list, kept for 15 s between the screens that read it. */
const activeCampaigns = (limit: number) =>
   fresh('campaigns', 15000, allCampaignRows).then((list) => {
      /* An empty answer is not kept: the next read asks again. */
      if (!list.length) recent.delete('campaigns')
      return list.slice(0, limit)
   })

/** The organization's numbers, once per screen. */
const inventory = () => fresh('inventory', 15000, () => api.inventoryNumbers())

const listOf = (v: unknown): Record<string, unknown>[] => {
   if (Array.isArray(v)) return v as Record<string, unknown>[]
   const inner = (v as { data?: unknown } | null)?.data
   return Array.isArray(inner) ? (inner as Record<string, unknown>[]) : []
}

const ago = (iso: unknown) => {
   const t = Date.parse(String(iso || ''))
   if (Number.isNaN(t)) return '—'
   const mins = Math.round((Date.now() - t) / 60000)
   if (mins < 60) return `${Math.max(mins, 1)} min ago`
   const hrs = Math.round(mins / 60)
   if (hrs < 24) return `${hrs} hr ago`
   return `${Math.round(hrs / 24)} days ago`
}

/* ---------- Business categories (lead types) ---------- */

const TYPE_MATCH: Record<TLeadType, RegExp> = {
   buyer: /buy/i,
   seller: /sell|listing/i,
   rental: /rent|tenant|landlord|lease/i,
}
let categoryCache: TBusinessTypeIds | null = null
/* Keyed by category and phase: new and cold leads get different words. */
const recommendedCache = new Map<
   string,
   { playbook: IPlaybook; bound: IBoundContent }
>()

export const categoryIds = async (): Promise<TBusinessTypeIds> => {
   if (categoryCache) return categoryCache
   const list = await api.businessCategories()
   const pick = (t: TLeadType) =>
      list.find((c) => TYPE_MATCH[t].test(`${c.slug || ''} ${c.name}`))?.id
   const ids = {
      buyer: pick('buyer'),
      seller: pick('seller'),
      rental: pick('rental'),
   }
   categoryCache = ids as TBusinessTypeIds
   return categoryCache
}

const categoryFor = (ids: TBusinessTypeIds, types: TLeadType[]) => {
   const missing = types.filter((t) => !ids[t])
   if (missing.length)
      throw new Error(
         `Your account has no category for ${missing.join(' and ')} leads yet. Ask support to add it, or untick it.`
      )
   return ids[types[0]]
}

/* ---------- Content ---------- */

const hashes = (pb: IPlaybook): Record<TContentSlot, string> => ({
   phone: JSON.stringify([
      pb.scriptName,
      pb.opening ?? '',
      pb.script.map((s) => [s.title, s.text, s.type ?? 'call_outbound']),
   ]),
   strategy: JSON.stringify([
      pb.messageGroupName,
      pb.texts.map((t) => t.text),
      pb.voicemails.map((v) => v.text),
   ]),
   conversation: pb.replyInstructions,
})

const refOf = (rev: IContentRevision): IContentReference => ({
   id: String(rev.contentId),
   revisionId: rev.id,
})

/** The library phase a campaign type's words are written for. */
const phaseOf = (kind: TCampaignKind | undefined) =>
   kind === 'revive' ? 'ff-reactivate' : 'ff-lead'

/** Items written for this phase first, then items with no phase, then the rest. */
const byPhase = <T extends ICatalogItem>(items: T[], phase: string): T[] =>
   [...items].sort((a, b) => {
      const rank = (p: ICatalogItem) =>
         p.campaignPhases?.includes(phase)
            ? 0
            : p.campaignPhases?.length
              ? 2
              : 1
      return rank(a) - rank(b)
   })

/** An outbound call script the assistant can use. */
const isOutboundAi = (p: ICatalogItem) =>
   (p.callDirections || ['outbound']).includes('outbound') &&
   (!p.callerTypes || p.callerTypes.includes('ai'))
/** The canonical scripts are the Gemini pairs ("Buyer-General-Gemini" …); within a phase they come first. */
const geminiRank = (p: ICatalogItem) => (/gemini/i.test(p.name || '') ? 0 : 1)
const pickOutbound = (items: ICatalogItem[], phase = 'ff-lead') => {
   const rank = (p: ICatalogItem) =>
      p.campaignPhases?.includes(phase) ? 0 : p.campaignPhases?.length ? 2 : 1
   return items
      .filter(isOutboundAi)
      .sort((a, b) => rank(a) - rank(b) || geminiRank(a) - geminiRank(b))[0]
}
/** Library names start with the lead type they are written for ("Seller-HomeValuation"). */
const NOT_FOR: Record<TLeadType, RegExp> = {
   buyer: /^(live|test|demo)?[\s_-]*(seller|rental|renter|lend|recruit)/i,
   seller: /^(live|test|demo)?[\s_-]*(buyer|rental|renter|lend|recruit)/i,
   rental: /^(live|test|demo)?[\s_-]*(buyer|seller|lend|recruit)/i,
}
/**
 * Only items written for this lead type: tagged with its category and not
 * named for another one. Buyers see buyer scripts, sellers seller scripts.
 */
export const forType = <T extends ICatalogItem>(
   items: T[],
   type: TLeadType,
   categoryId: number
): T[] =>
   items.filter(
      (i) =>
         (i.businessTypeId === undefined || i.businessTypeId === categoryId) &&
         !NOT_FOR[type].test(i.name.trim())
   )
/** Whether a script can greet callers the engine has not matched to a lead. */
const greetsUnknown = (p: Pick<ICatalogItem, 'leadTypes'>) =>
   !!p.leadTypes?.includes('unknown')
/*
 * Production refuses to publish a pool whose inbound script cannot greet
 * unknown callers, so a script written for both comes first (an earlier
 * campaign's own copy, for instance); a known-only one is customized on save.
 */
const pickInbound = (items: ICatalogItem[], phase = 'ff-lead') =>
   byPhase(
      items.filter(
         (p) =>
            (p.callDirections || []).includes('inbound') &&
            (!p.leadTypes || p.leadTypes.includes('known'))
      ),
      phase
   ).sort((a, b) => Number(greetsUnknown(b)) - Number(greetsUnknown(a)))[0]

/**
 * The words the screens show come from the library's recommended items for
 * the first lead type. The references are kept so an unchanged playbook is
 * used by reference and nothing new is written to the library.
 */
const loadRecommended = async (
   type: TLeadType,
   categoryId: number,
   phase = 'ff-lead'
): Promise<{ playbook: IPlaybook; bound: IBoundContent } | null> => {
   const [phones, strategies, conversations] = await Promise.all([
      api
         .catalog('phone', categoryId)
         .then((l) => forType(l, type, categoryId)),
      api
         .catalog('strategy', categoryId)
         .then((l) => forType(l, type, categoryId)),
      api.catalog('conversation', categoryId),
   ])
   const outbound = pickOutbound(phones, phase)
   const inbound = pickInbound(phones, phase)
   /*
    * Strategy: a buyer whose organization has property matches set up gets
    * "the ride" (its own Property Match words); otherwise the canonical
    * Gemini strategy, then whatever fits the phase.
    */
   const ride =
      type === 'buyer'
         ? byPhase(strategies, phase).find(
              (s) => !s.shared && /property.?match|\bride\b/i.test(s.name || '')
           )
         : undefined
   const phaseRank = (p: ICatalogItem) =>
      p.campaignPhases?.includes(phase) ? 0 : p.campaignPhases?.length ? 2 : 1
   const strategy =
      ride ??
      [...strategies].sort(
         (a, b) => phaseRank(a) - phaseRank(b) || geminiRank(a) - geminiRank(b)
      )[0]
   /* A category may have no reply instructions yet; the built-in ones are written on save. */
   const conversation = conversations[0] ?? null
   if (!outbound || !strategy) return null
   const acquire = (kind: TContentKind, item: ICatalogItem) =>
      api.acquireRevision(kind, item.contentId)
   const [p, s, c, i] = await Promise.all([
      acquire('phone', outbound),
      acquire('strategy', strategy),
      conversation
         ? acquire('conversation', conversation)
         : Promise.resolve(null),
      inbound ? acquire('phone', inbound) : Promise.resolve(null),
   ])
   const snapshot: IContentSnapshot = {
      phone: {
         name: outbound.name,
         definition: p.definition as IPhoneDefinition,
      },
      strategy: {
         name: strategy.name,
         definition: s.definition as IStrategyDefinition,
      },
      conversation:
         conversation && c
            ? {
                 name: conversation.name,
                 definition: c.definition as IConversationDefinition,
              }
            : null,
   }
   const playbook = {
      ...playbookFromContent(snapshot, RECOMMENDED_PLAYBOOK),
      /* So the script and message-group pickers show what is in use. */
      scriptId: String(outbound.contentId),
      messageGroupId: String(strategy.contentId),
   }
   return {
      playbook,
      bound: {
         categoryId,
         hashes: hashes(playbook),
         refs: {
            outboundScript: refOf(p),
            strategy: refOf(s),
            ...(c ? { conversation: refOf(c) } : {}),
            inboundScript: i ? refOf(i) : null,
         } as IPlaybookRefs,
         inboundForUnknown: !!inbound && greetsUnknown(inbound),
      },
   }
}

/**
 * Make sure the library holds exactly the words in the draft, then return
 * the references to put in the configuration. Only the slots whose words
 * changed are written: a recommended (shared) or earlier item is customized
 * into this organization's own copy; with nothing to start from, it is
 * created. Each reference is stored as soon as it comes back, because content
 * calls take no idempotency key.
 */
const ensureContent = async (
   draft: IFlowDraft,
   type: TLeadType,
   categoryId: number
): Promise<IPlaybookRefs | null> => {
   const pb = viewFor(draft.playbook, type)
   const now = hashes(pb)
   const key = String(categoryId)
   const base = session.boundBy?.[key] ?? session.recommendedBy?.[key] ?? null
   const campaign = draft.audience.name || 'New campaign'
   /* Each lead type's items are named after it, so they are told apart in the library. */
   const suffix =
      audienceTypes(draft.audience).length > 1
         ? ` · ${LEAD_TYPE_LABEL[type]}`
         : ''
   const name = `${campaign}${suffix}`
   /* The user's own copy is saved under the name they gave it, as is (the editor proposes "<library item> · <campaign>"). */
   const own = (given: string) =>
      pb.origin === 'mine' && given.trim()
         ? { name: `${given.trim()}${suffix}` }
         : {}
   const defs = {
      phone: () => ({
         ...toPhoneDefinition(pb, categoryId, name),
         ...own(pb.scriptName),
      }),
      strategy: () => ({
         ...toStrategyDefinition(pb, categoryId, name),
         ...own(pb.messageGroupName),
      }),
      conversation: (): IConversationDefinition => ({
         name: `${name} - Reply instructions`,
         businessTypeId: categoryId,
         content: pb.replyInstructions,
      }),
   }
   const slotRef: Record<TContentSlot, keyof IPlaybookRefs> = {
      phone: 'outboundScript',
      strategy: 'strategy',
      conversation: 'conversation',
   }
   const bound: IBoundContent = base
      ? { ...base, hashes: { ...base.hashes }, refs: { ...base.refs } }
      : {
           categoryId,
           hashes: { phone: '', strategy: '', conversation: '' },
           refs: {} as IPlaybookRefs,
        }
   for (const slot of ['phone', 'strategy', 'conversation'] as const) {
      const current = bound.refs[slotRef[slot]] as IContentReference | undefined
      if (current && bound.hashes[slot] === now[slot]) continue
      const rev = current
         ? await api.customizeContent(current, defs[slot]())
         : await api.createContent(slot, defs[slot]())
      ;(bound.refs as unknown as Record<string, IContentReference>)[
         slotRef[slot]
      ] = refOf(rev)
      bound.hashes[slot] = now[slot]
      remember({ boundBy: { ...session.boundBy, [key]: bound } })
   }
   /*
    * Seen on production: "Pool pool-default requires an unknown-caller inbound
    * script". The shared inbound scripts are written for known callers only,
    * so the one in use is customized once into this organization's own copy
    * that greets both; the copy is then found first by pickInbound.
    */
   if (bound.refs.inboundScript && !bound.inboundForUnknown) {
      const rev = await api.customizeContent(bound.refs.inboundScript, {
         name: `${name} - Incoming call`,
         leadTypes: ['known', 'unknown'],
      })
      bound.refs.inboundScript = refOf(rev)
      bound.inboundForUnknown = true
      remember({ boundBy: { ...session.boundBy, [key]: bound } })
   }
   remember({ boundBy: { ...session.boundBy, [key]: bound } })
   return bound.refs
}

/* ---------- Voice ---------- */

/**
 * The flow offers exactly two voices (CTO 2026-10-28): Erin (Gemini Kore,
 * the catalog's own row) and Kyle (Gemini Iapetus — CV3's male assistant
 * voice, not yet in the public catalog, so its row is spelled here with
 * its recorded demo).
 */
const KYLE = {
   voiceId: 'Iapetus',
   name: 'Kyle',
   description: 'Hi, this is Kyle',
   gender: 'male',
   accent: '',
   language: 'English',
   age: '',
   avatar: '',
   previewUrl:
      'https://forward-flow-assets.s3.us-west-2.amazonaws.com/recordings/Kyle+FF+Demo.wav',
   createdAt: '',
}
const ERIN_FALLBACK = {
   ...KYLE,
   voiceId: 'Kore',
   name: 'Erin',
   description: 'Hi, this is Erin',
   gender: 'female',
   previewUrl:
      'https://forward-flow-assets.s3.us-west-2.amazonaws.com/recordings/Erin+FF+Demo.wav',
}

/** GET /svc/campaigns/voices/public, curated to the two offered voices. */
const publicVoices = async () => {
   const all = listOf(
      await fresh('voices', 60000, () =>
         getData<unknown>('/svc/campaigns/voices/public')
      ).catch(() => null)
   )
      .filter((r) => !r.status || r.status === 'active')
      .map((r) => ({
         voiceId: String(r.voice_id ?? r.voiceId ?? ''),
         name: String(r.name ?? 'Voice'),
         description: String(r.description ?? ''),
         gender: String(r.gender ?? ''),
         accent: String(r.accent ?? ''),
         language: String(r.language ?? ''),
         age: String(r.age ?? ''),
         avatar: String(r.avatar ?? ''),
         previewUrl: String(r.preview_url ?? r.previewUrl ?? ''),
         createdAt: String(r.created_at ?? r.createdAt ?? ''),
      }))
      .filter((v) => v.voiceId)
   return [all.find((v) => v.name === 'Erin') ?? ERIN_FALLBACK, KYLE]
}

/** The public catalog, or null when it is unavailable (so the older list is used). */
const latestVoices = async () => {
   const list = await publicVoices()
   return list.length ? list : null
}

let voiceCache: IVoiceOption[] | null = null

const orNothing = async <T>(load: () => Promise<T>, fallback: T) => {
   try {
      return await load()
   } catch {
      return fallback
   }
}

/**
 * The voice's profile from the catalog (description, gender, language,
 * accent, avatar), stored on the assistant the way the earlier campaign
 * screen stored it; the callers read the assistant, not the catalog.
 */
const voiceProfile = async (
   voiceId: string
): Promise<Partial<Omit<IAssistant, 'id'>>> => {
   const pub = (await orNothing(publicVoices, [])).find(
      (v) => v.voiceId === voiceId
   )
   const v =
      pub ??
      (await orNothing(() => api.voices(), [])).find(
         (x) => x.voiceId === voiceId
      )
   if (!v) return {}
   const text = (x: string | null | undefined) => (x ? x : undefined)
   return {
      description: text(v.description),
      gender: text(v.gender),
      age: text(v.age),
      language: text(v.language) ?? 'English',
      accent: text(v.accent) ?? 'General American',
      avatar: text(v.avatar),
      previewUrl: text(v.previewUrl),
      designation: 'Sales assistant',
   }
}

const ensureAssistant = async (campaignId: string, draft: IFlowDraft) => {
   const { optionId, name } = draft.voice
   if (!optionId || !name.trim()) return
   const a = session.assistant
   if (a && a.voiceId === optionId && a.name === name) return
   const body = {
      assistantName: name.trim(),
      voiceId: optionId,
      ...(await voiceProfile(optionId)),
   }
   if (a) {
      await api.updateAssistant(campaignId, a.id, body)
      remember({ assistant: { id: a.id, voiceId: optionId, name } })
      return
   }
   const created = await api.addAssistant(campaignId, body)
   remember({ assistant: { id: created.id, voiceId: optionId, name } })
}

/* ---------- Numbers ---------- */

/** Numbers seen in search results, so a pick can be reserved with the row the backend gave. */
const seen = new Map<string, IAvailableNumber>()

const syncNumbers = async (campaignId: string, draft: IFlowDraft) => {
   const wanted = draft.numbers.selected
      .filter((n) => !n.owned)
      .map((n) => toE164(n.number))
   const reserved = session.reserved || []
   const toReserve = wanted.filter((n) => !reserved.includes(n))
   const toDrop = reserved.filter((n) => !wanted.includes(n))
   if (toDrop.length) await api.removeNumbers(campaignId, toDrop)
   const kept = reserved.filter((n) => !toDrop.includes(n))
   for (const n of toReserve) {
      let row = seen.get(n)
      if (!row) {
         const again = await api.availableNumbers(areaOf(n))
         again.forEach((r) => seen.set(toE164(r.phoneNumber), r))
         row = seen.get(n)
      }
      if (!row)
         throw new Error(
            `${toDisplay(n)} was just taken. Pick another number in Phone numbers.`
         )
      await api.reserveNumber(campaignId, row)
      kept.push(n)
      remember({ reserved: [...kept] })
   }
   remember({ reserved: kept })
   /*
    * Numbers the organization already owns are attached, not bought (the
    * shipped workspace does the same); listed in the configuration alone,
    * the backend's readiness preview counted them as purchases.
    */
   const ownedWanted = draft.numbers.selected
      .filter((n) => n.owned)
      .map((n) => toE164(n.number))
   const assigned = session.assigned || []
   const toAssign = ownedWanted.filter((n) => !assigned.includes(n))
   if (toAssign.length) {
      await api.assignNumbers(campaignId, toAssign)
      remember({ assigned: [...assigned, ...toAssign] })
   }
   return toReserve.length > 0 || toDrop.length > 0 || toAssign.length > 0
}

/* ---------- Results, timing and voices from the existing analytics ---------- */

/** GET returning the envelope's `data`. */
const getData = async <T>(apiPath: string, params?: Record<string, unknown>) =>
   (
      await apiGet<{ data?: T }>({
         apiPath,
         ...(params ? { config: { params } } : {}),
      })
   )?.data as T

type TStatsResponse = {
   series?: TDayRow[]
   totals?: Record<string, number>
   campaigns?: Array<Record<string, unknown>>
}

/** Org-wide daily outreach (GET /campaigns/stats?from&to), with a per-campaign breakdown. */
const orgStats = (from: string, to: string) =>
   getData<TStatsResponse>('/campaigns/stats', { from, to })

/** One campaign's daily outreach (GET /campaigns/outreach/:id/stats?from&to). */
const campaignSeries = async (id: string, from: string, to: string) =>
   listOf(
      (
         await getData<TStatsResponse>(
            `/campaigns/outreach/${encodeURIComponent(id)}/stats`,
            { from, to }
         )
      )?.series
   ) as TDayRow[]

/** All-time leads and contracts for one campaign (the campaign page's own stats). */
const campaignSnapshot = async (id: string) => {
   try {
      const r = (await CampaignService.getCampaignByLeadSource(
         id,
         'campaign'
      )) as unknown as {
         campaignStats?: { topMetrics?: { current?: Record<string, number> } }
      }
      const c = r?.campaignStats?.topMetrics?.current || {}
      return { leads: num(c.totalLeads), contracts: num(c.totalContracts) }
   } catch {
      return { leads: 0, contracts: 0 }
   }
}

/** The hour calls connect best for a campaign (UTC from the backend), in local time. */
const bestHour = async (campaignId: string) => {
   const rows = await getData<Array<{ hour?: number }>>(
      `/analytics/best-time-to-call/${encodeURIComponent(campaignId)}`,
      { statType: 'campaign' }
   )
   const h = listOf(rows)[0]?.hour
   return typeof h === 'number' ? localHour(h) : null
}

/** For a new campaign: the best hour of the organization's first live campaign. */
let orgBestHour: Promise<string | null> | null = null
const bestHourForOrg = () => {
   if (!orgBestHour)
      orgBestHour = activeCampaigns(25)
         .then((list) => list.find((c) => c.status === 'active'))
         .then((c) => (c ? bestHour(String(c.id)) : null))
         .catch(() => null)
   return orgBestHour
}

/**
 * Appointment rate per voice: each live campaign reports appointments and
 * leads per assistant (GET /analytics/top-agents/:id); assistants are matched
 * to their voice through the campaign's assistants.
 */
const voicePerformance = async () => {
   const live = (await activeCampaigns(25))
      .filter((c) => c.status === 'active')
      .slice(0, 10)
   const totals = new Map<string, { appts: number; leads: number }>()
   await Promise.all(
      live.map(async (c) => {
         const data = (c.data || {}) as Record<string, unknown>
         const voiceOf = new Map(
            listOf(data.aiAssistants).map((a) => [
               String(a.id),
               String(a.voiceId || a.voice_id || ''),
            ])
         )
         const rows = listOf(
            await getData<unknown>(
               `/analytics/top-agents/${encodeURIComponent(String(c.id))}`,
               { page: 1, limit: 50, statType: 'campaign' }
            ).catch(() => [])
         )
         rows.forEach((r) => {
            const voice = voiceOf.get(String(r.assistantId))
            if (!voice) return
            const t = totals.get(voice) || { appts: 0, leads: 0 }
            t.appts += num(r.totalAppointments)
            t.leads += num(r.totalAssigned)
            totals.set(voice, t)
         })
      })
   )
   const out = new Map<string, number>()
   totals.forEach((t, voice) => {
      /* Too few leads to call it a rate. */
      if (t.leads >= 20)
         out.set(voice, Math.round((t.appts / t.leads) * 1000) / 10)
   })
   return out
}

/* ---------- Where the leads live ---------- */

const countOf = (criteria: ICriteria[]) =>
   apiPost<{ data?: { count?: number | string } }>({
      apiPath: '/active-lead-sources/estimated-leads',
      data: criteria,
   }).then((r) => num(r?.data?.count))

/** How many leads the audience holds, in parts that do not overlap (utils/live-derive.ts). */
const countAudience = async (audience: IAudience) => {
   const counts = await Promise.all(countQueries(audience).map(countOf))
   return counts.reduce((a, b) => a + b, 0)
}

/** Lead phones are stored as "+1XXX…" or bare "XXX…", so a prefix is counted in both forms. */
const PHONE_FORMS = ['+1', '']
const DIGITS = [...'0123456789']

/**
 * How many of the audience's leads have a phone starting with each prefix
 * (an area code, or part of one). The count endpoint takes `phone LIKE`
 * and ANDs it with the audience's own criteria; OR is not grouped, so each
 * form is its own query.
 */
const prefixCounts = async (audience: IAudience, prefixes: string[]) => {
   const parts = countQueries(audience)
   const out = new Map<string, number>()
   await Promise.all(
      prefixes.map(async (prefix) => {
         const counts = await Promise.all(
            parts.flatMap((part) =>
               PHONE_FORMS.map((form) =>
                  countOf([
                     ...part,
                     {
                        field: 'phone',
                        operator: 'LIKE',
                        value: `${form}${prefix}%`,
                        logic: 'AND',
                     },
                  ]).catch(() => 0)
               )
            )
         )
         out.set(
            prefix,
            counts.reduce((a, b) => a + b, 0)
         )
      })
   )
   return out
}

/**
 * Finds the area codes most leads live in a digit at a time, following the
 * busiest branch at each level (8 + 10 + 10 prefixes, two phone forms each).
 */
const discoverCodes = async (audience: IAudience, total: number) => {
   let level = await prefixCounts(audience, [...'23456789'])
   for (let depth = 1; depth < 3; depth += 1) {
      const follow = [...level.entries()]
         .filter(([, n]) => n > 0 && n >= total / 5)
         .sort((a, b) => b[1] - a[1])
         .slice(0, 1)
      if (!follow.length) return new Map<string, number>()
      level = await prefixCounts(
         audience,
         follow.flatMap(([p]) => DIGITS.map((d) => p + d))
      )
   }
   return level
}

/** Where the leads live, kept a minute per audience: a save does not move them. */
const locality = new Map<string, { at: number; value: Promise<IAreaCode[]> }>()
/** How many leads the audience held when its codes were counted. */
const localityTotals = new Map<string, number>()
const localityOf = (key: string, load: () => Promise<IAreaCode[]>) => {
   const hit = locality.get(key)
   if (hit && Date.now() - hit.at < 60000) return hit.value
   /* A count that fails (an organization whose count endpoint rejects a criterion) leaves the numbers unpicked, never the step broken. */
   const value = load().catch(() => {
      locality.delete(key)
      return [] as IAreaCode[]
   })
   locality.set(key, { at: Date.now(), value })
   return value
}

/** US area codes never start with 0 or 1 ("171" is a +1 number read wrong). */
const isAreaCode = (code: string) => /^[2-9]\d{2}$/.test(code)
/** Toll-free codes say nothing about where a lead lives, and nobody calls from one. */
const isLocalCode = (code: string) =>
   isAreaCode(code) && !/^8(00|33|44|55|66|77|88)$/.test(code)

/**
 * The shares the organization's campaigns already see: this campaign's
 * overview, else the live campaigns' overviews, else its own numbers.
 */
const overviewShares = async () => {
   const shares = new Map<string, number>()
   const add = (list: Array<{ areaCode: string; percentage: number }>) =>
      list.forEach((t) => {
         const code = String(t.areaCode)
         if (!isAreaCode(code)) return
         shares.set(code, (shares.get(code) || 0) + num(t.percentage))
      })
   if (session.campaignId) {
      const o = await CampaignService.getCampaignOverview(
         session.campaignId
      ).catch(() => null)
      add(o?.topAreaCodes || [])
   }
   if (!shares.size) {
      const live = (await activeCampaigns(25).catch(() => [])).filter(
         (c) => c.status === 'active'
      )
      const overviews = await Promise.all(
         live
            .slice(0, 5)
            .map((c) =>
               CampaignService.getCampaignOverview(String(c.id)).catch(
                  () => null
               )
            )
      )
      overviews.forEach((o) => add(o?.topAreaCodes || []))
   }
   return shares
}

/** The number search shouts its cities ("BUENA VISTA"); people read "Buena Vista". */
export const cityCase = (city: unknown) => {
   const s = String(city || '')
   return s === s.toUpperCase()
      ? s.toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase())
      : s
}

/** The city an area code serves, from the number search. */
const cityCache = new Map<string, Promise<string>>()
const cityOf = (code: string) => {
   if (!cityCache.has(code))
      cityCache.set(
         code,
         api.availableNumbers(code).then((rows) => {
            rows.forEach((r) => seen.set(toE164(r.phoneNumber), r))
            return cityCase(rows[0]?.locality || rows[0]?.region || '')
         })
      )
   return cityCache.get(code) as Promise<string>
}

/** A voice's style as a short two-word label, like the design's "Warm Consultative". */
export const shortStyle = (
   description?: string | null,
   _gender?: string | null,
   accent?: string | null,
   name?: string | null
) => {
   /* Descriptions are sometimes a greeting ("Hi, This is Erin"): skip greetings and the voice's own name. */
   const skip = new RegExp(
      `^(the|and|with|for|voice|that|this|who|has|are|you|your|hi|hello|hey|its|is${name ? `|${name}` : ''})$`,
      'i'
   )
   const words = (description || '')
      .replace(/[^A-Za-z\s-]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !skip.test(w))
      .slice(0, 2)
   if (words.length)
      return words
         .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
         .join(' ')
   return accent
      ? `${accent.charAt(0).toUpperCase()}${accent.slice(1)} accent`
      : 'Natural voice'
}

/** The next three steps a live campaign takes, in the words the live page uses. */
export const nextUp = (
   cadence: ICadenceStep[],
   best: string | null,
   enrolled: number
) => {
   let calls = 0
   let texts = 0
   return [...cadence]
      .sort((a, b) => a.day - b.day || a.order - b.order)
      .slice(0, 3)
      .map((step) => {
         const time = best ? timeFor(step.action, best) : step.localTime
         const day =
            step.day <= 1
               ? 'Today'
               : step.day === 2
                 ? 'Tomorrow'
                 : `Day ${step.day}`
         let what: string
         if (step.action === 'call') {
            calls += 1
            what =
               calls === 1
                  ? `First calls to ${enrolled.toLocaleString()} leads`
                  : `Call ${calls}, picking up where each lead left off`
         } else {
            texts += 1
            what =
               texts === 1
                  ? 'Text 1 to anyone who did not answer'
                  : `Text ${texts} to anyone still quiet`
         }
         return { when: `${day} · ${clockTime(time)}`, what }
      })
}

/** The dashboard's campaign_type list → the one type the flow shows. */
const kindOf = (types: unknown): TCampaignKind | null => {
   const list = (Array.isArray(types) ? types : types ? [types] : []).map(
      String
   )
   return list.includes('activity')
      ? 'activity'
      : list.includes('revive')
        ? 'revive'
        : list.includes('new-lead')
          ? 'new-lead'
          : null
}

/* ---------- Results, the same way for the organization and one campaign ---------- */

/** A ratio as a percent metric (answered calls, replied texts). */
const percentMetric = (
   series: TDayRow[],
   w: ReturnType<typeof periodWindows>,
   top: (r: TDayRow) => number,
   bottom: (r: TDayRow) => number
): IMetric => {
   const m = ratioMetric(series, w, top, bottom)
   const r1 = (x: number) => Math.round(x * 1000) / 10
   return {
      value: r1(m.value),
      previous: r1(m.previous),
      trend: m.trend.map(r1),
   }
}

const resultsFrom = (series: TDayRow[], period: TPeriod): IResults => {
   const w = periodWindows(period)
   return {
      period,
      leads: windowMetric(series, w, PICK.leads),
      conversations: windowMetric(series, w, PICK.conversations),
      appointments: windowMetric(series, w, PICK.appointments),
      transfers: windowMetric(series, w, PICK.transfers),
      attemptsPerLead: ratioMetric(series, w, PICK.touches, PICK.leads),
      callsAnswered: percentMetric(
         series,
         w,
         (r) => num(r.calls_connected),
         (r) => num(r.calls_total)
      ),
      textsReplied: percentMetric(
         series,
         w,
         (r) => num(r.sms_replies),
         (r) => num(r.sms_sent)
      ),
   }
}

/* ---------- What needs attention ---------- */

type TConversationRow = Record<string, unknown> & {
   leadId?: number | string
   id?: number | string
   firstName?: string
   lastName?: string
   lastConversation?: { date?: string; content?: string | null; type?: string }
   lastFfConversation?: {
      date?: string
      preview?: string | null
      direction?: string
      channel?: string
   }
   meta?: { appointment?: { date?: string } }
   handoff?: { lastAt?: string }
   engagement?: { lastLeadReplyAt?: string }
   assignedCampaignId?: string | number
}

const short = (text: unknown = '', n = 90) => {
   const value = typeof text === 'string' ? text : ''
   return value.length > n ? `${value.slice(0, n - 1).trimEnd()}…` : value
}

/**
 * Recent bookings, live transfers and replies for one campaign,
 * from the conversation list (GET /recent-conversations filtered by
 * campaignId), newest first.
 */
const recentActivityNow = async (
   campaignId?: string
): Promise<IActivityItem[]> => {
   const since = new Date(Date.now() - 30 * 864e5).toISOString()
   const ask = (params: Record<string, unknown>) =>
      getData<unknown>('/recent-conversations', {
         page: 1,
         limit: 8,
         ...(campaignId ? { campaignId } : {}),
         ...params,
      }).then((d) =>
         (listOf(d) as TConversationRow[]).filter(
            (r) => !campaignId || String(r.assignedCampaignId) === campaignId
         )
      )
   const [booked, transferred, replied] = await Promise.all([
      ask({
         hasAppointment: 'true',
         appointmentStatus: 'upcoming,completed',
         sortBy: 'latest_activity',
      }),
      ask({
         liveTransfer: 'true',
         handoffStatus: 'success',
         sortBy: 'latest_activity',
      }),
      ask({
         textConversations: 'true',
         lastResponseFrom: since,
         sortBy: 'latest_response',
      }),
   ])
   const item = (
      kind: TActivityKind,
      r: TConversationRow,
      text: string,
      at?: string
   ): IActivityItem => {
      const leadId = String(r.leadId ?? r.id ?? '')
      const inCampaign = r.assignedCampaignId ?? campaignId
      return {
         id: `${kind}-${leadId}`,
         kind,
         leadId,
         ...(inCampaign ? { campaignId: String(inCampaign) } : {}),
         leadName:
            [r.firstName, r.lastName].filter(Boolean).join(' ') || 'A lead',
         text,
         at: at || r.lastConversation?.date || '',
         ...(!at ? { timeLabel: 'Last activity' } : {}),
      }
   }
   const when = (iso?: string) =>
      iso
         ? new Date(parseDate(iso)).toLocaleString(undefined, {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit',
           })
         : ''
   // Legacy conversation rows do not identify the sender of their preview. Read the
   // inbound timeline item rather than displaying our last outbound SMS as a reply.
   const replies: IActivityItem[] = []
   for (let offset = 0; offset < replied.length; offset += 4) {
      const batch = await Promise.all(
         replied.slice(offset, offset + 4).map(async (r) => {
            const leadId = String(r.leadId ?? r.id ?? '')
            if (!leadId) return null
            const latest = r.lastFfConversation
            if (
               latest?.direction === 'inbound' &&
               ['sms', 'email'].includes(latest.channel || '') &&
               latest.date
            ) {
               return item(
                  'reply',
                  r,
                  short(latest.preview) || 'Replied',
                  latest.date
               )
            }
            const timeline = listOf(
               await getData<unknown>(
                  `/conversations-timeline/${encodeURIComponent(leadId)}`,
                  {
                     page: 1,
                     limit: 25,
                     hideExternalContent: true,
                  }
               )
            )
            const reply = timeline
               .filter(
                  (event) =>
                     event.isIncoming === true &&
                     ['text', 'email'].includes(String(event.itemType)) &&
                     event.externalContent !== true
               )
               .sort(
                  (a, b) => Date.parse(String(b.ts)) - Date.parse(String(a.ts))
               )[0]
            return reply
               ? item(
                    'reply',
                    r,
                    short(reply.content) || 'Replied',
                    String(reply.ts || '')
                 )
               : null
         })
      )
      batch.forEach((reply) => {
         if (reply) replies.push(reply)
      })
   }
   return [
      ...booked.map((r) =>
         item(
            'appointment',
            r,
            `Booked an appointment${r.meta?.appointment?.date ? ` for ${when(r.meta.appointment.date)}` : ''}`
         )
      ),
      ...transferred.map((r) =>
         item('transfer', r, 'Sent live to your team', r.handoff?.lastAt)
      ),
      ...replies,
   ]
      .filter((x) => x.leadId && parseDate(x.at) >= Date.parse(since))
      .sort((x, y) => parseDate(y.at) - parseDate(x.at))
      .slice(0, 12)
}

/**
 * Recent bookings, live transfers and replies for one campaign,
 * from the conversation list (GET /recent-conversations filtered by
 * campaignId), newest first.
 */
const recentActivity = (campaignId?: string): Promise<IActivityItem[]> =>
   fresh(`activity:${campaignId || 'all'}`, 15000, () =>
      recentActivityNow(campaignId)
   )

/* ---------- Opening a saved campaign ---------- */

/** The saved cadence as touches; emails are not part of the flow. */
const touchesFrom = (cadence: ICadenceStep[]): ITouch[] =>
   cadence
      .filter((c) => c.action === 'call' || c.action === 'sms')
      .map((c) => ({
         id: c.id,
         action: c.action === 'call' ? 'call' : 'text',
         day: c.day,
         time: c.localTime,
         ...(c.action === 'call'
            ? { voicemail: c.voicemail === 'if-configured' }
            : {}),
      }))

/** The preset a cadence still matches (same days, actions and voicemails), else custom. */
const presetOf = (touches: ITouch[]): TCadencePreset => {
   const shape = (list: ITouch[]) =>
      sortTouches(list)
         .map((t) => `${t.day}:${t.action}:${t.voicemail ? 'vm' : ''}`)
         .join(',')
   const presets = Object.keys(CADENCE_PRESETS) as Array<
      Exclude<TCadencePreset, 'custom'>
   >
   return (
      presets.find(
         (p) => shape(CADENCE_PRESETS[p].touches) === shape(touches)
      ) ?? 'custom'
   )
}

/** The words of one content pool, read by the references the campaign pins. */
const contentOfPool = async (
   pool: IContentPool
): Promise<{
   content: TPlaybookContent
   inboundForUnknown: boolean
} | null> => {
   const readRef = (r: IContentReference | undefined) =>
      r
         ? api.readRevision(r.revisionId, r.id).catch(() => null)
         : Promise.resolve(null)
   const [p, st, c, i] = await Promise.all([
      readRef(pool.outboundScripts[0]),
      readRef(pool.strategies[0]),
      readRef(pool.conversationContent[0]),
      readRef(pool.inboundScripts[0]),
   ])
   if (!p && !st) return null
   const phone = p?.definition as IPhoneDefinition | undefined
   const strategy = st?.definition as IStrategyDefinition | undefined
   const conversation = c?.definition as IConversationDefinition | undefined
   const script = (phone?.sections || [])
      .filter((x) => x.type === 'call_outbound')
      .sort((x, y) => x.order - y.order)
      .map((x, n) => ({
         id: String(x.sectionId ?? `s${n + 1}`),
         title: x.name,
         text: fromTemplate(x.content),
      }))
   return {
      content: {
         /* These are the campaign's own words: the Who step must not swap them for the library's. */
         origin: 'mine',
         scriptName: phone?.name || RECOMMENDED_PLAYBOOK.scriptName,
         messageGroupName:
            strategy?.name || RECOMMENDED_PLAYBOOK.messageGroupName,
         scriptId: pool.outboundScripts[0]?.id,
         messageGroupId: pool.strategies[0]?.id,
         script: script.length ? script : RECOMMENDED_PLAYBOOK.script,
         texts: (strategy?.sms || []).map((m, n) => ({
            id: `s${n + 1}`,
            text: fromTemplate(m.template),
         })),
         voicemails: (strategy?.voicemail || []).map((m, n) => ({
            id: `v${n + 1}`,
            text: fromTemplate(m.template),
         })),
         replyInstructions:
            conversation?.content || RECOMMENDED_PLAYBOOK.replyInstructions,
      },
      inboundForUnknown: !!(
         i?.definition as IPhoneDefinition | undefined
      )?.leadTypes?.includes('unknown'),
   }
}

const FILTER_FIELDS = [
   'source',
   'stage',
   'tags',
   'assigned_pond_id',
   'inquired_property',
]

/** The backend answers 409 "Legacy campaigns use the existing editor" for campaigns made before v2. */
const isLegacy = (e: unknown) =>
   e instanceof CampaignV2Error && e.status === 409

const statusOf = (status: string): IFlowDraft['status'] =>
   status === 'active' ? 'active' : status === 'paused' ? 'paused' : 'draft'

const ownedNumber = (n: string): IPhoneNumberOption => ({
   number: toDisplay(n),
   areaCode: areaOf(n),
   city: '',
   owned: true,
})

/* ---------- Saving ---------- */

const rememberRead = (read: IConfigurationRead) =>
   remember({ campaignId: read.campaignId, revision: read.revision })

/**
 * Create the draft once, then save the whole configuration against its
 * revision. A 409 means someone else saved in between: the latest revision is
 * read and the user's draft saved on top of it once ("keep mine").
 */
const saveConfiguration = async (draft: IFlowDraft) => {
   const ids = await categoryIds()
   categoryFor(ids, audienceTypes(draft.audience))
   /* Each lead type's words are written side by side (they are independent library items). */
   const refs: TPlaybookRefsByType = {}
   await Promise.all(
      audienceTypes(draft.audience).map(async (t) => {
         const r = await ensureContent(draft, t, ids[t])
         if (r) refs[t] = r
      })
   )
   const base = toConfiguration(draft, ids, refs)
   /* Every step goes out at the hour calls connect best (the heatmap), not a fixed time. */
   const best =
      (session.campaignId
         ? await bestHour(session.campaignId).catch(() => null)
         : null) ?? (await bestHourForOrg())
   const configuration = {
      ...base,
      source:
         base.source.mode === 'dynamic'
            ? {
                 mode: 'dynamic' as const,
                 criteria: [
                    ...kindCriteria(
                       draft.audience.kinds,
                       new Date(),
                       draft.audience.ageDays
                    ),
                    ...base.source.criteria,
                 ],
              }
            : base.source,
      cadence: best
         ? base.cadence.map((c) => ({
              ...c,
              localTime: timeFor(c.action, best),
           }))
         : base.cadence,
   }
   const name = draft.audience.name || 'New campaign'

   if (!session.campaignId) {
      if (!session.createKey)
         remember({ createKey: idempotencyKey('campaign-flow-draft') })
      const read = await api.createDraft(
         name,
         configuration,
         session.createKey as string
      )
      rememberRead(read)
      remember({ createKey: undefined })
      return read.campaignId
   }
   const id = session.campaignId
   try {
      rememberRead(
         await api.saveConfiguration(
            id,
            session.revision ?? 0,
            configuration,
            name
         )
      )
   } catch (e) {
      if (!(e instanceof CampaignV2Error) || e.status !== 409) throw e
      const fresh = await api.readConfiguration(id)
      rememberRead(
         await api.saveConfiguration(id, fresh.revision, configuration, name)
      )
   }
   return id
}

/* ---------- The service ---------- */

export const campaignFlowLive: ICampaignFlowService = {
   context: async () => {
      const [user, price] = await Promise.all([
         UserService.getUserData().catch(() => null),
         api.pricing(1).catch(() => null),
      ])
      const org = orgId()
      const membership = (user?.userOrganizations || []).find(
         (o) => o.organizationId === org
      )
      /* An operator is not a member: the organization's own record carries the name. */
      let detailsName = ''
      if (!membership?.organization?.name && org) {
         const d = await getData<Record<string, unknown>>(
            `/organizations/${org}/details`
         ).catch(() => null)
         detailsName = String(
            d?.name ?? d?.friendly_business_name ?? d?.businessName ?? ''
         )
      }
      const first = (session.reserved || [])[0]
      return {
         org: {
            organization:
               String(
                  membership?.organization?.name ||
                     detailsName ||
                     ORG.organization
               )
                  .replace(/\s*[-–·]\s*[0-9a-f]{24,}\s*$/i, '')
                  .trim() || ORG.organization,
            agentName: user?.firstName || ORG.agentName,
            ownerFirstName: user?.firstName || ORG.ownerFirstName,
            phoneNumber: first ? toDisplay(first) : ORG.phoneNumber,
         },
         lead: { ...SAMPLE_LEAD },
         numberPrice:
            typeof price?.currentPeriod?.amount === 'number'
               ? price.currentPeriod.amount
               : NUMBER_PRICE_PER_MONTH,
      }
   },

   /* Entry */

   campaigns: async (period = '30d', onQuick) => {
      const w = periodWindows(period)
      const [list, org] = await Promise.all([
         activeCampaigns(Number.MAX_SAFE_INTEGER),
         orgStats(w.currentFrom, w.to).catch(() => null),
      ])
      const byId = new Map(
         listOf(org?.campaigns).map((c) => [String(c.campaignId), c])
      )
      const rows = list
      const rowOf = (
         c: Record<string, unknown>,
         read: ILiveCampaign | null,
         daily: TDayRow[],
         snap: { leads: number; contracts: number }
      ): ICampaignRow => {
         const data = (c.data || {}) as Record<string, unknown>
         const assistant: { voiceId?: string; assistantName?: string } =
            read?.aiAssistants?.[0] ?? listOf(data.aiAssistants)[0] ?? {}
         const voiceId = String(assistant.voiceId || '')
         const range = byId.get(String(c.id)) || {}
         const status = String(c.status || read?.status || '')
         return {
            id: String(c.id),
            name: String(
               c.campaignName || read?.campaignName || c.source || 'Campaign'
            ),
            kind: kindOf(
               c.campaignType ?? read?.campaignType ?? data.campaign_type
            ),
            /* The compact list carries no audience or voice; do not imply "All sources". */
            source: String(c.source || ''),
            status:
               status === 'active'
                  ? 'live'
                  : status === 'paused'
                    ? 'paused'
                    : 'draft',
            voiceHue: voiceId ? hueOf(voiceId) : 262,
            voiceName: String(assistant.assistantName || ''),
            /* Leads in the campaign now; the all-time count when today's gauge is missing. */
            leads: num(range.leads_assigned) || snap.leads,
            reached: num(range.leads_contacted),
            appointments: num(range.appointments_ff),
            transfers: num(range.handoffs_success),
            trend: buckets(daily, w.currentFrom, w.days, PICK.appointments),
            lastActivity:
               lastActive(daily) ??
               (status === 'active'
                  ? ago(c.lastUpdated ?? read?.lastUpdated)
                  : 'Not started'),
         }
      }
      /* The table can show now; the voice, the trend and the last activity follow. */
      const none = { leads: 0, contracts: 0 }
      onQuick?.(rows.map((c) => rowOf(c, null, [], none)))
      /*
       * The list answers only id, name and status (seen on production), so
       * the voice, numbers and last change come from each campaign's own
       * read, all at once. A draft has never called anyone: no daily series,
       * no gauge. Asking for both on every row made the page wait on two
       * calls per campaign.
       */
      const ran = (c: Record<string, unknown>) =>
         c.status === 'active' || c.status === 'paused'
      /* Eight campaigns at a time, so an account with many drafts does not fire a hundred reads at once. */
      const inBatches = async <T, R>(
         items: T[],
         each: (item: T) => Promise<R>
      ): Promise<R[]> => {
         const out: R[] = []
         for (let i = 0; i < items.length; i += 8)
            out.push(...(await Promise.all(items.slice(i, i + 8).map(each))))
         return out
      }
      const [details, series, snapshots] = await Promise.all([
         inBatches(rows, (c) =>
            Promise.resolve()
               .then(() => api.readCampaign(String(c.id)))
               .catch(() => null)
         ),
         inBatches(rows, (c) =>
            ran(c)
               ? campaignSeries(String(c.id), w.currentFrom, w.to).catch(
                    () => []
                 )
               : Promise.resolve([] as TDayRow[])
         ),
         inBatches(rows, (c) =>
            ran(c) && !byId.has(String(c.id))
               ? campaignSnapshot(String(c.id))
               : Promise.resolve({ leads: 0, contracts: 0 })
         ),
      ])
      return rows.map((c, i) => rowOf(c, details[i], series[i], snapshots[i]))
   },

   overview: async (period) => {
      const w = periodWindows(period)
      const [org, rows] = await Promise.all([
         orgStats(w.from, w.to),
         CampaignService.getActiveCampaigns({
            page: 1,
            limit: 100,
            status: 'active',
         })
            .then(listOf)
            .catch(() => []),
      ])
      return {
         ...resultsFrom(listOf(org?.series) as TDayRow[], period),
         liveCampaigns: rows.filter((c) => c.status === 'active').length,
      } satisfies IOverviewStats
   },

   starters: async () => [],

   /* Who we call */

   /* The keyword reader runs on the organization's own sources, tags, stages and ponds. */
   interpretDescription: async (text) => interpretDescription(text),

   /*
    * Kinds, lead types and filter rows together. "New or recently active" is
    * not one query on this endpoint, so it is counted in parts that do not
    * overlap and added up (utils/live-derive.ts).
    */
   estimateLeads: countAudience,

   filterVocabulary: async (): Promise<TFilterVocabulary> => {
      const org = orgId()
      const safe = <T>(p: Promise<T>) => p.catch(() => [] as unknown as T)
      const [sources, stages, tags, ponds] = await Promise.all([
         safe(FubService.getPeopleSources({ orgId: org, page: 1, limit: 500 })),
         safe(
            OnboardingV2Service.getFubStages({
               organizationId: org,
               limit: 500,
               page: 1,
            })
         ),
         safe(
            OnboardingV2Service.getPeopleTags({
               orgId: org,
               page: 1,
               limit: 1000,
            })
         ),
         safe(FubService.getPonds({ orgId: org, page: 1, limit: 500 })),
      ])
      const opts = (
         rows: Record<string, unknown>[],
         value: (r: Record<string, unknown>) => unknown,
         label: (r: Record<string, unknown>) => unknown = value
      ) =>
         rows
            .map((r) => ({
               value: String(value(r) ?? ''),
               label: String(label(r) ?? ''),
            }))
            .filter((o) => o.value && o.label)
      return {
         source: opts(listOf(sources), (r) => r.leadSource),
         stage: opts(listOf(stages), (r) => r.stageName),
         tags: opts(listOf(tags), (r) => r.tagName),
         assigned_pond_id: opts(
            listOf(ponds),
            (r) => r.pondId ?? r.id,
            (r) => r.pondName ?? r.name ?? r.pondId ?? r.id
         ),
         inquired_property: [],
      }
   },

   /* Playbook */

   recommendedPlaybook: async (audience) => {
      const clone = <T>(v: T) => JSON.parse(JSON.stringify(v)) as T
      const types: TLeadType[] = audienceTypes(audience)
      /* One lead type's recommended words, or the built-in ones when the library has none. */
      const forType = async (t: TLeadType) => {
         try {
            const ids = await categoryIds()
            const categoryId = categoryFor(ids, [t])
            const phase = phaseOf(audience.kinds[0])
            let found = recommendedCache.get(`${categoryId}:${phase}`)
            if (!found) {
               const loaded = await loadRecommended(t, categoryId, phase)
               if (!loaded) return null
               found = loaded
               recommendedCache.set(`${categoryId}:${phase}`, found)
            }
            remember({
               recommendedBy: {
                  ...session.recommendedBy,
                  [String(categoryId)]: found.bound,
               },
            })
            return clone(found.playbook)
         } catch {
            /* Library unavailable: start from the built-in words; they are written to the library on save. */
            return null
         }
      }
      const loaded = await Promise.all(types.map(forType))
      const first = loaded[0] ?? clone(RECOMMENDED_PLAYBOOK)
      return withTypes(
         {
            ...first,
            byType: Object.fromEntries(
               types.map((t, i) => [
                  t,
                  loaded[i]
                     ? pickContent(loaded[i] as IPlaybook)
                     : clone(RECOMMENDED_BY_TYPE[t]),
               ])
            ),
         },
         types
      )
   },

   /*
    * The library for one lead type: Forward Flow's shared items and this
    * organization's own, from the catalog (`shared` tells them apart).
    */
   contentOptions: async (type, slot) => {
      const categoryId = categoryFor(await categoryIds(), [type])
      const items = forType(
         await fresh(`catalog:${slot}:${categoryId}`, 60000, () =>
            api.catalog(slot === 'script' ? 'phone' : 'strategy', categoryId)
         ),
         type,
         categoryId
      )
      return (slot === 'script' ? items.filter(isOutboundAi) : items).map(
         (i): IContentOption => ({
            id: String(i.contentId),
            name: i.name,
            owner: i.shared ? 'recommended' : 'yours',
            /* The catalog row does not always carry the texts; "0 texts" would mislead. */
            summary:
               slot === 'messages' &&
               i.counts &&
               i.counts.sms + i.counts.voicemail > 0
                  ? `${plural(i.counts.sms, 'text')} · ${plural(i.counts.voicemail, 'voicemail')}`
                  : undefined,
         })
      )
   },

   /*
    * Pick a library item: its words come into the playbook and its revision is
    * kept as the reference, so saving an unchanged pick writes nothing new.
    */
   loadContent: async (type, slot, id) => {
      const categoryId = categoryFor(await categoryIds(), [type])
      const kind = slot === 'script' ? 'phone' : 'strategy'
      const [items, rev] = await Promise.all([
         api.catalog(kind, categoryId).catch(() => [] as ICatalogItem[]),
         api.acquireRevision(kind, Number(id)),
      ])
      const name = items.find((i) => String(i.contentId) === id)?.name
      const key = String(categoryId)
      const base: IBoundContent = session.boundBy?.[key] ??
         session.recommendedBy?.[key] ?? {
            categoryId,
            hashes: { phone: '', strategy: '', conversation: '' },
            refs: {} as IPlaybookRefs,
         }
      const bound: IBoundContent = {
         ...base,
         hashes: { ...base.hashes },
         refs: { ...base.refs },
      }
      /* The same reading and the same hash as the recommended words, so an unchanged pick saves by reference. */
      let out: Partial<TPlaybookContent>
      if (slot === 'script') {
         const { opening, script } = scriptFromPhone(
            rev.definition as IPhoneDefinition
         )
         const scriptName = name ?? 'Call script'
         bound.refs.outboundScript = refOf(rev)
         bound.hashes.phone = hashes({
            ...RECOMMENDED_PLAYBOOK,
            scriptName,
            opening,
            script,
         }).phone
         out = { opening, script, scriptId: id, scriptName }
      } else {
         const { texts, voicemails } = messagesFromStrategy(
            rev.definition as IStrategyDefinition
         )
         const messageGroupName = name ?? 'Message group'
         bound.refs.strategy = refOf(rev)
         bound.hashes.strategy = hashes({
            ...RECOMMENDED_PLAYBOOK,
            messageGroupName,
            texts,
            voicemails,
         }).strategy
         out = { texts, voicemails, messageGroupId: id, messageGroupName }
      }
      remember({ boundBy: { ...session.boundBy, [key]: bound } })
      return out
   },

   /* Test phone: no customer-safe test endpoint yet (gap G4); the drawer simulates in the browser. */
   sendTestText: async () => {
      throw new Error('Test texts to your phone are not available yet.')
   },
   placeTestCall: async () => {
      throw new Error('Test calls to your phone are not available yet.')
   },

   /* Voice */

   /*
    * The current public voice catalog (the one onboarding uses), newest first,
    * with its recorded previews. The campaign-assistant list is the fallback:
    * it drops previews and dates.
    */
   voices: async () => {
      const [catalog, perf] = await Promise.all([
         latestVoices().catch(() => null),
         voicePerformance().catch(() => new Map<string, number>()),
      ])
      const list =
         catalog ??
         (await api.voices()).map((v) => ({
            voiceId: v.voiceId,
            name: v.name,
            description: v.description ?? '',
            gender: v.gender ?? '',
            accent: v.accent ?? '',
            language: v.language ?? '',
            age: v.age ?? '',
            avatar: v.avatar ?? '',
            previewUrl: v.previewUrl ?? '',
            createdAt: '',
         }))
      const newest = [...list].sort((a, b) =>
         b.createdAt.localeCompare(a.createdAt)
      )[0]
      const options = list.map((v): IVoiceOption => ({
         id: v.voiceId,
         defaultName: v.name,
         style: shortStyle(v.description, v.gender, v.accent, v.name),
         hue: hueOf(v.voiceId),
         appointmentRate: perf.get(v.voiceId) ?? 0,
         previewUrl: v.previewUrl || null,
         gender: v.gender || null,
         ...(newest?.createdAt && v === newest ? { isNew: true } : {}),
      }))
      /* Best performing first, marked, as the design shows. */
      const best = [...options].sort(
         (a, b) => b.appointmentRate - a.appointmentRate
      )[0]
      if (best && best.appointmentRate > 0) best.bestPerforming = true
      voiceCache = [
         ...options.filter((o) => o.bestPerforming),
         ...options.filter((o) => !o.bestPerforming),
      ]
      return voiceCache
   },

   previewVoice: async (voiceId) => {
      const list = voiceCache || (await campaignFlowLive.voices())
      return list.find((v) => v.id === voiceId)?.previewUrl ?? null
   },

   /* Numbers */

   /*
    * Where the leads live, counted from the audience itself: the count
    * endpoint takes a phone prefix, so each candidate area code is counted
    * with the audience's own criteria. Candidates are the organization's
    * numbers and the codes its campaigns already see (at most 8, so 16
    * counts). With `discover`, when those hold under half of the leads, the
    * codes are found digit by digit (up to 56 more counts), which the
    * Numbers step asks for while it shows "Finding where your leads live".
    * An audience not chosen yet falls back to the shares the campaigns see.
    * Each code gets its city from the number search.
    */
   areaCodes: async (audience, options) => {
      const key = JSON.stringify([
         audience.kinds,
         audience.leadTypes,
         audience.filters,
         audience.activity,
      ])
      if (options?.discover) {
         /* The candidates' counts are shared by both reads of the same audience. */
         const plain = await campaignFlowLive.areaCodes(audience)
         const counted = plain.length > 0 && plain[0].leads !== undefined
         if (!counted || !countQueries(audience).length) return plain
         const total = localityTotals.get(key) ?? 0
         const covered = plain.reduce((a, c) => a + (c.leads ?? 0), 0)
         if (total === 0 || covered >= total / 2) return plain
         return localityOf(`${key}:discover`, async () => {
            const found = await discoverCodes(audience, total)
            const counts = new Map(plain.map((c) => [c.code, c.leads ?? 0]))
            found.forEach((n, code) =>
               counts.set(code, Math.max(counts.get(code) ?? 0, n))
            )
            const top = [...counts.entries()]
               .filter(([code, n]) => n > 0 && isLocalCode(code))
               .sort((a, b) => b[1] - a[1])
               .slice(0, 5)
            const cities = await Promise.all(
               top.map(([code]) => cityOf(code).catch(() => ''))
            )
            return top.map(([code, n], i) => ({
               code,
               city: cities[i],
               leads: n,
               leadShare: Math.round((n / Math.max(total, n)) * 100),
            }))
         })
      }
      return localityOf(key, async () => {
         const [prior, owned] = await Promise.all([
            overviewShares().catch(() => new Map<string, number>()),
            inventory().catch(() => []),
         ])
         const withCities = async (
            rows: Array<Omit<IAreaCode, 'city'>>
         ): Promise<IAreaCode[]> => {
            const cities = await Promise.all(
               rows.map((r) => cityOf(r.code).catch(() => ''))
            )
            return rows.map((r, i) => ({ ...r, city: cities[i] }))
         }
         if (countQueries(audience).length) {
            const candidates = [
               ...new Set([
                  ...owned.map((n) => areaOf(n.phoneNumber)),
                  ...prior.keys(),
               ]),
            ]
               .filter(isLocalCode)
               .slice(0, 8)
            /* Counted here, not taken from the draft: the Who step's count may still be running. */
            const [total, counts] = await Promise.all([
               countAudience(audience),
               candidates.length
                  ? prefixCounts(audience, candidates)
                  : Promise.resolve(new Map<string, number>()),
            ])
            localityTotals.set(key, total)
            const top = [...counts.entries()]
               .filter(([code, n]) => n > 0 && isLocalCode(code))
               .sort((a, b) => b[1] - a[1])
               .slice(0, 5)
            if (top.length)
               return withCities(
                  top.map(([code, n]) => ({
                     code,
                     leads: n,
                     leadShare: Math.round((n / Math.max(total, n)) * 100),
                  }))
               )
         }
         const shares = new Map(prior)
         if (!shares.size)
            owned.forEach((n) => {
               const code = areaOf(n.phoneNumber)
               if (isAreaCode(code))
                  shares.set(code, (shares.get(code) || 0) + 1)
            })
         const total = [...shares.values()].reduce((a, b) => a + b, 0) || 1
         return withCities(
            [...shares.entries()]
               .sort((a, b) => b[1] - a[1])
               .slice(0, 5)
               .map(([code, share]) => ({
                  code,
                  leadShare: Math.round((share / total) * 100),
               }))
         )
      })
   },

   /* Area code ("719"), city ("Colorado Springs") or state ("CO"). */
   availableNumbers: async (search) => {
      const q = search.trim()
      if (!q) return []
      const digits = q.replace(/\D/g, '')
      /* "Austin, TX" is not understood by the search; the city alone is. */
      const term =
         digits.length >= 3 ? digits.slice(0, 3) : q.split(',')[0].trim()
      const rows = await api.availableNumbers(term)
      return rows
         .filter((r) => r.phoneNumber)
         .map((r): IPhoneNumberOption => {
            const e164 = toE164(r.phoneNumber)
            seen.set(e164, r)
            return {
               number: toDisplay(e164),
               areaCode: areaOf(e164),
               city: cityCase(r.locality || r.region || ''),
            }
         })
   },

   /*
    * Numbers the organization has bought and no campaign uses. A number only
    * reserved for a draft is bought when that draft launches, and one with no
    * status cannot be attached ("Select purchased numbers belonging to this
    * organization", seen on production): neither is owned here. The
    * organization's own line (`ffNumber`) is not a campaign number.
    */
   ownedNumbers: async () =>
      (await inventory())
         .filter(
            (n) =>
               !n.isUsedInCampaigns &&
               !n.isOrganizationNumber &&
               n.status === 'purchased'
         )
         .map((n) => ({
            number: toDisplay(n.phoneNumber),
            areaCode: areaOf(n.phoneNumber),
            city: cityCase(n.locality || n.region || ''),
            owned: true,
         })),

   /* Who gets the leads */

   teammates: async () =>
      (await api.agents()).map((a) => ({
         id: a.crmUserId,
         name: a.name,
         initials:
            a.name
               .split(/\s+/)
               .map((w) => w[0])
               .join('')
               .slice(0, 2)
               .toUpperCase() || '?',
      })),

   /* Lifecycle */

   /*
    * A saved campaign, opened for editing: the configuration and the
    * campaign are read once, the words come from the content each pool pins,
    * and the session remembers the revision, the content references and the
    * assistant so saving an unchanged draft writes nothing new.
    */
   loadDraft: async (id) => {
      startNewLiveSession()
      const [cfg, campaign, ids] = await Promise.all([
         api.readConfiguration(id).catch((e: unknown) => {
            if (isLegacy(e)) return null
            throw e
         }),
         api.readCampaign(id).catch(() => null),
         /* An environment without the content categories (the route is newer
            than some deployments) still opens the campaign: the lead types
            just come back unknown and the Who step asks. */
         categoryIds().catch(() => ({}) as TBusinessTypeIds),
      ])
      if (!cfg) {
         /* Made before the flow: what the campaign itself says, and its voice is all that saves. */
         if (!campaign) throw new Error('Could not open this campaign.')
         remember({ campaignId: id, legacy: true })
         const assistant = campaign.aiAssistants[0]
         if (assistant)
            remember({
               assistant: {
                  id: assistant.id,
                  voiceId: assistant.voiceId,
                  name: assistant.assistantName,
               },
            })
         const numbers = campaign.phoneNumbers.map(ownedNumber)
         const routing = campaign.agentAssignmentConfig
         const draft: IFlowDraft = {
            ...emptyDraft(),
            id,
            status: statusOf(campaign.status),
            legacy: true,
            audience: {
               ...emptyDraft().audience,
               name: campaign.campaignName,
            },
            voice: assistant
               ? { optionId: assistant.voiceId, name: assistant.assistantName }
               : emptyDraft().voice,
            numbers: {
               areaCodes: [...new Set(numbers.map((n) => n.areaCode))],
               selected: numbers,
            },
            handoff: {
               mode:
                  routing?.strategy === 'jump_ball'
                     ? 'first_to_answer'
                     : 'round_robin',
               agentIds: routing?.agentIds || [],
            },
         }
         const done: TRailKey[] = ['who', 'handoff']
         if (assistant) done.push('voice')
         return { draft, done }
      }
      rememberRead(cfg)
      const c = cfg.configuration
      const typeOf = (categoryId: number) =>
         (Object.keys(ids) as TLeadType[]).find((t) => ids[t] === categoryId)
      const types = (c.businessTypeIds || [])
         .map(typeOf)
         .filter((t): t is TLeadType => !!t)
      const criteria = c.source?.mode === 'dynamic' ? c.source.criteria : []
      /* The campaign's type is the created_at line the flow wrote; without one the Who step asks. */
      const age = criteria.find((x) => x.field === 'created_at')
      const kinds: TCampaignKind[] = !age
         ? []
         : ['>=', '>'].includes(age.operator)
           ? ['new-lead']
           : ['revive']
      /*
       * The line's own distance in days, so a saved "under 20 days"
       * campaign re-saves as 20, not the 30-day default.
       */
      const ageLineDays = age
         ? Math.round(
              (Date.now() - new Date(String(age.value)).getTime()) / 864e5
           )
         : NaN
      const ageDays =
         Number.isFinite(ageLineDays) && ageLineDays > 0 ? ageLineDays : undefined
      const filters = criteria
         .filter((x) => FILTER_FIELDS.includes(x.field))
         .map((x) => ({
            field: x.field as IFilterRow['field'],
            operator: x.operator as IFilterRow['operator'],
            value: x.value as IFilterRow['value'],
            logic: 'AND' as const,
         }))
      const numbers = (
         campaign?.phoneNumbers?.length
            ? campaign.phoneNumbers
            : c.phoneNumbers || []
      ).map(ownedNumber)
      /* Its numbers are attached already: a save does not attach them again. */
      remember({
         assigned: numbers.filter((n) => n.owned).map((n) => toE164(n.number)),
      })
      const assistant = campaign?.aiAssistants?.[0]
      if (assistant)
         remember({
            assistant: {
               id: assistant.id,
               voiceId: assistant.voiceId,
               name: assistant.assistantName,
            },
         })

      /* Words per lead type from the pools; the first type's are mirrored at the top. */
      const pools = (c.contentPools || []).filter((pool) =>
         typeOf(pool.businessTypeId)
      )
      const loaded = await Promise.all(pools.map(contentOfPool))
      const byType: IPlaybook['byType'] = {}
      const boundBy: Record<string, IBoundContent> = {}
      pools.forEach((pool, i) => {
         const t = typeOf(pool.businessTypeId) as TLeadType
         const got = loaded[i]
         if (!got) return
         byType[t] = got.content
         boundBy[String(pool.businessTypeId)] = {
            categoryId: pool.businessTypeId,
            hashes: { phone: '', strategy: '', conversation: '' },
            refs: {
               outboundScript: pool.outboundScripts[0],
               strategy: pool.strategies[0],
               conversation: pool.conversationContent[0],
               inboundScript: pool.inboundScripts[0] ?? null,
            } as IPlaybookRefs,
            inboundForUnknown: got.inboundForUnknown,
         }
      })
      const touches = touchesFrom(c.cadence || [])
      const firstType = types[0]
      const first = firstType ? byType[firstType] : undefined
      const playbook = syncMessages({
         ...RECOMMENDED_PLAYBOOK,
         ...(first ?? {}),
         preset: touches.length
            ? presetOf(touches)
            : RECOMMENDED_PLAYBOOK.preset,
         touches: touches.length ? touches : RECOMMENDED_PLAYBOOK.touches,
         usedByTeams: 0,
         ...(Object.keys(byType).length ? { byType } : {}),
      })
      /* Fingerprints of the words as saved, so an unchanged draft is written by reference. */
      Object.values(boundBy).forEach((b) => {
         const t = typeOf(b.categoryId) as TLeadType
         b.hashes = hashes(viewFor(playbook, t))
      })
      remember({ boundBy })

      const draft: IFlowDraft = {
         id,
         status: statusOf(String(campaign?.status || cfg.status || 'draft')),
         audience: {
            description: '',
            name: cfg.name || campaign?.campaignName || '',
            kinds,
            leadTypes:
               types.length && types.length < LEAD_TYPES.length ? types : [],
            filters,
            ...(ageDays !== undefined ? { ageDays } : {}),
            leadCount: null,
         },
         playbook,
         voice: assistant
            ? { optionId: assistant.voiceId, name: assistant.assistantName }
            : emptyDraft().voice,
         numbers: {
            areaCodes: [...new Set(numbers.map((n) => n.areaCode))],
            selected: numbers,
         },
         handoff: {
            mode:
               c.routing?.strategy === 'jump_ball'
                  ? 'first_to_answer'
                  : 'round_robin',
            agentIds: c.routing?.agentIds || [],
         },
      }
      const done: TRailKey[] = ['who']
      if (draft.handoff.agentIds.length) done.push('handoff')
      if (assistant) done.push('voice')
      if (Object.keys(byType).length) done.push('playbook')
      if (numbers.length) done.push('numbers')
      return { draft, done }
   },

   saveDraft: async (draft) => {
      forgetReads()
      if (draft.id && session.campaignId && draft.id !== session.campaignId)
         startNewLiveSession()
      /*
       * A campaign made before the flow has no configuration to write; its
       * assistant is updated. Seen on production: the update answers 200 with
       * the new values but the campaign keeps the old ones, so the campaign is
       * read back and the user told when the change did not stick.
       */
      if (draft.legacy && draft.id && session.legacy) {
         await ensureAssistant(draft.id, draft)
         const after = await api.readCampaign(draft.id).catch(() => null)
         const a = after?.aiAssistants?.[0]
         if (
            a &&
            (a.voiceId !== draft.voice.optionId ||
               a.assistantName !== draft.voice.name.trim())
         ) {
            remember({
               assistant: {
                  id: a.id,
                  voiceId: a.voiceId,
                  name: a.assistantName,
               },
            })
            throw new Error(
               'The backend accepted the new voice but kept the old one: campaigns made before the guided flow cannot change voice yet. Reported to the backend team.'
            )
         }
         return { id: draft.id }
      }
      if (draft.id && !session.campaignId) {
         const [read, campaign] = await Promise.all([
            api.readConfiguration(draft.id),
            api.readCampaign(draft.id).catch(() => null),
         ])
         rememberRead(read)
         /* The campaign's assistant, so a voice change updates it instead of adding a second. */
         const a = campaign?.aiAssistants?.[0]
         if (a && !session.assistant)
            remember({
               assistant: {
                  id: a.id,
                  voiceId: a.voiceId,
                  name: a.assistantName,
               },
            })
      }
      const id = await saveConfiguration(draft)
      await ensureAssistant(id, draft)
      /* Reserving changes the configuration on the server, so read the new revision. */
      if (await syncNumbers(id, draft))
         rememberRead(await api.readConfiguration(id))
      return { id }
   },

   review: async (draft) => {
      const items = localReadiness(draft)
      let ready = items.every((i) => i.ok)
      let toBuy = draft.numbers.selected.filter((n) => !n.owned).length
      if (session.campaignId) {
         const [p, owned] = await Promise.all([
            api.preview(session.campaignId),
            inventory().catch(() => []),
         ])
         ready = ready && p.ready
         /* The backend lists the numbers it would buy; ones the organization owns cost nothing. */
         const mine = new Set(
            owned
               .filter((n) => n.status === 'purchased')
               .map((n) => toE164(n.phoneNumber))
         )
         toBuy = p.purchaseNumbers?.length
            ? p.purchaseNumbers.filter((n) => !mine.has(toE164(n))).length
            : p.purchaseNumbers
              ? 0
              : p.numbersToPurchase
         p.reasons.forEach((r) => items.push(reasonToItem(r)))
      }
      const price = toBuy ? await api.pricing(toBuy).catch(() => null) : null
      const amount = price?.currentPeriod?.amount
      return {
         ready,
         items,
         costs: [
            {
               label: `${toBuy} new number${toBuy === 1 ? '' : 's'}`,
               amount:
                  typeof amount === 'number'
                     ? `$${amount.toFixed(2)}/month`
                     : toBuy
                       ? 'Charged when you launch'
                       : '$0',
            },
            {
               label: 'Calls, texts and voicemails',
               amount: 'Included in your plan',
            },
         ],
      }
   },

   activate: async () => {
      forgetReads()
      const id = session.campaignId
      if (!id) throw new Error('Save the campaign before launching it.')
      const p = await api.preview(id)
      if (!p.ready)
         throw new Error(
            p.reasons.map((r) => reasonToItem(r).label).join(' ') ||
               'The campaign is not ready to launch yet.'
         )
      /* The same key on every retry, so a timed-out launch never runs twice. */
      if (!session.activateKey)
         remember({ activateKey: idempotencyKey(`activate-${id}`) })
      await api.activate(id, session.activateKey as string)
      remember({ activateKey: undefined })
   },

   setPaused: async (id, paused) => {
      forgetReads()
      if (paused) return api.setStatus(id, 'paused')
      await api.activate(id, idempotencyKey(`resume-${id}`))
   },

   /* One campaign's page: the same results as the organization's, plus what it is set up to do. */
   campaignStats: async (id, period) => {
      const w = periodWindows(period)
      let legacy = false
      const [series, snap, cfg, campaign, ids] = await Promise.all([
         campaignSeries(id, w.from, w.to).catch(() => [] as TDayRow[]),
         campaignSnapshot(id),
         api.readConfiguration(id).catch((e: unknown) => {
            legacy = isLegacy(e)
            return null
         }),
         api.readCampaign(id).catch(() => null),
         categoryIds().catch(() => null),
      ])
      const c = cfg?.configuration
      const status = String(campaign?.status || cfg?.status || '')
      const typeOf = (categoryId: number) =>
         (Object.keys(ids || {}) as TLeadType[]).find(
            (t) => ids?.[t] === categoryId
         )
      const types = (c?.businessTypeIds || [])
         .map(typeOf)
         .filter((t): t is TLeadType => !!t)
      /* The saved campaign's type: created_at >= line is new, < line is cold. */
      const age =
         c?.source.mode === 'dynamic'
            ? c.source.criteria.find((x) => x.field === 'created_at')
            : null
      const isNew = !!age && ['>=', '>'].includes(age.operator)
      const kind = !age ? 'All leads' : isNew ? 'New leads' : 'Cold leads'
      const filters =
         c?.source.mode === 'dynamic'
            ? c.source.criteria.filter((x) => x.field !== 'created_at').length
            : 0
      const who =
         types.length && types.length < 3
            ? types.map((t) => LEAD_TYPE_LABEL[t]).join(' and ')
            : 'Every lead type'
      const cadence = (c?.cadence || []).filter((x) => x.action !== 'email')
      const calls = cadence.filter((x) => x.action === 'call').length
      const days = cadence.length ? Math.max(...cadence.map((x) => x.day)) : 0
      return {
         ...resultsFrom(series, period),
         name: campaign?.campaignName || cfg?.name || 'Campaign',
         kind: !age ? null : isNew ? 'new-lead' : 'revive',
         voiceName: campaign?.aiAssistants?.[0]?.assistantName || undefined,
         voiceHue: campaign?.aiAssistants?.[0]?.voiceId
            ? hueOf(String(campaign.aiAssistants[0].voiceId))
            : undefined,
         status:
            status === 'active'
               ? 'live'
               : status === 'paused'
                 ? 'paused'
                 : 'draft',
         enrolled: snap.leads,
         ...(legacy ? { legacy } : {}),
         setup: {
            audience: `${kind} · ${who}${filters ? ` · ${plural(filters, 'filter')}` : ''}`,
            timing: cadence.length
               ? `${plural(cadence.length, 'touch', 'touches')} over ${plural(days, 'day')} · ${plural(calls, 'call')}, ${plural(cadence.length - calls, 'text')}`
               : 'No timing yet',
            numbers: (campaign?.phoneNumbers || c?.phoneNumbers || []).map(
               toDisplay
            ),
            handoff:
               c?.routing?.agentIds?.length === 1
                  ? 'One phone, every time'
                  : c?.routing?.strategy === 'jump_ball'
                    ? 'Team, first to pick up'
                    : c?.routing?.agentIds?.length
                      ? 'Team, in turns'
                      : 'Nobody yet',
         },
      } satisfies ICampaignStats
   },

   recentActivity: (campaignId) => recentActivity(campaignId),
}
